import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, tap } from 'rxjs/operators';
import { StorageService } from '../../services/storage.service';
import { StorageKeys } from '../../services/storage.service';
import * as kioskActions from './kiosk.actions';
import type { KioskSettings } from './kiosk.state';

@Injectable()
export class KioskEffects {
	private actions$ = inject(Actions);
	private storageService = inject(StorageService);

	// Load settings from localStorage
	loadKioskSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(kioskActions.loadKioskSettings),
			map(() => {
				const settings = this.loadSettingsFromStorage();
				return kioskActions.loadKioskSettingsSuccess({ settings });
			})
		)
	);

	// Sync settings to localStorage
	updateKioskSettings$ = createEffect(() =>
		this.actions$.pipe(
			ofType(kioskActions.updateKioskSettings),
			tap(({ settings }) => {
				this.storageService.setItem(StorageKeys.kioskSettingsData, JSON.stringify(settings));
			}),
			map(({ settings }) => kioskActions.updateKioskSettingsSuccess({ settings }))
		)
	);

	// Sync location to localStorage
	setKioskLocation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(kioskActions.setKioskLocation),
			tap(({ locationId }) => {
				this.storageService.setItem(StorageKeys.kioskModeLocationId, locationId);
			}),
			map(({ locationId }) => kioskActions.setKioskLocationSuccess({ locationId }))
		)
	);

	// Clear location from localStorage
	clearKioskLocation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(kioskActions.clearKioskLocation),
			tap(() => {
				this.storageService.removeItem(StorageKeys.kioskModeLocationId);
			}),
			map(() => kioskActions.clearKioskLocationSuccess())
		)
	);

	private loadSettingsFromStorage(): KioskSettings {
		let settings = null;
		try {
			const stored = this.storageService.getItem(StorageKeys.kioskSettingsData);
			if (stored) {
				settings = JSON.parse(stored);
			}
		} catch (e) {
			console.log('Error loading kiosk settings from storage:', e);
		}

		if (settings == null || !this.areValidSettings(settings)) {
			return {
				findByName: true,
				findById: false,
				findByScan: false,
			};
		} else {
			return settings as KioskSettings;
		}
	}

	private areValidSettings(settings: any): boolean {
		return settings && typeof settings.findByName === 'boolean' && typeof settings.findById === 'boolean' && typeof settings.findByScan === 'boolean';
	}
}
